import { useFormik } from "formik";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useDrawMapStore, useMapShapeStore, usePipedriveStore, useTenantInMarketStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useCurrentOrFallbackPipedriveUserId from "../../hooks/useCurrentOrFallbackPipedriveUserId";
import useNavigateToTenantInMarket from "../../hooks/useNavigateToTenantInMarket";
import useTenantInMarketCreate from "../../hooks/useTenantInMarketCreate";
import useTenantInMarketValidationSchema from "../../hooks/useTenantInMarketValidationSchema";
import DetailsType from "../../interfaces/DetailsType";
import { ITenantInMarketState, TenantInMarketState } from "../../interfaces/ITenantInMarket";
import { ClosableOrder } from "../../store/ClosableStore";
import { DrawMapMode } from "../../store/DrawMapStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import { CreateControls } from "../EditControls";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import TenantInMarketFields from "./TenantInMarketFields";

function TenantInMarketCreate() {
    const navigate = useNavigate();
    const navigateToTenantInMarket = useNavigateToTenantInMarket();
    const createTenantInMarket = useTenantInMarketCreate();
    const tenantInMarketStore = useTenantInMarketStore();
    const drawMapStore = useDrawMapStore();
    const pipedriveStore = usePipedriveStore();
    const mapShapeStore = useMapShapeStore();
    const validationSchema = useTenantInMarketValidationSchema();
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();
    const initialState: ITenantInMarketState = useMemo(() => ({
        ...TenantInMarketState.empty(),
        assignedToPipedriveUserId: pipedriveUserId,
    }), [pipedriveUserId]);

    const formik = useFormik<ITenantInMarketState>({
        initialValues: initialState,
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            const createdTenantInMarketId = await createTenantInMarket(values);
            navigateToTenantInMarket(createdTenantInMarketId, true);
        },
    });

    useEffect(() => {
        if (initialState)
            formik.resetForm({ values: initialState });
    }, [initialState]);

    useEffect(() => {
        mapShapeStore.clear();
        drawMapStore.drawNewTenantInMarketShape();
        return () => drawMapStore.clear();
    }, []);

    useEffect(() => {
        if (drawMapStore.mode === DrawMapMode.None)
            close();
    }, [drawMapStore.mode]);

    const close = () => navigate("/");
    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    if (drawMapStore.mode !== DrawMapMode.NewTenantInMarketShape || !drawMapStore.result)
        return null;

    return (
        <DetailsPanel type={DetailsType.TenantInMarketCreate} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <TenantInMarketIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>Add New Tenant in Market</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <TenantInMarketFields formik={formik} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <CreateControls
                        isStateChanged={formik.dirty || drawMapStore.isResultChanged}
                        isCreateLoading={tenantInMarketStore.isTenantsInMarketLoading.creating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onCreate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}></div>
            </div>
        </DetailsPanel>
    );
}

export default observer(TenantInMarketCreate);